
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormFieldWrapper } from '@/components/ui/form-field-wrapper';
import { useProfile } from '@/contexts/profile';
import { useToast } from '@/hooks/use-toast';
import { User } from 'lucide-react';
import { ModalHeader, ModalBody, ModalFooter } from '@/components/ui/modal';

interface ProfileFormProps {
  onSuccess: () => void;
  onClose: () => void;
}

export const ProfileForm = ({ onSuccess, onClose }: ProfileFormProps) => {
  const { addProfile, setActiveProfile } = useProfile();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'personal' as 'personal' | 'business',
    color: '#3b82f6'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome do perfil é obrigatório",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const newProfile = await addProfile({
        ...formData,
        email: '',
        isActive: false,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });

      setActiveProfile(newProfile);

      toast({
        title: "Sucesso",
        description: `Perfil "${newProfile.name}" criado e selecionado com sucesso!`,
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ModalHeader
        title="Novo Perfil"
        description="Crie um novo perfil para organizar suas finanças"
        icon={User}
        onClose={onClose}
      />

      <ModalBody>
        <form id="profile-form" onSubmit={handleSubmit} className="space-y-6">
          <FormFieldWrapper label="Nome do Perfil" required>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Pessoal, Empresa XYZ"
              className="rounded-xl"
              disabled={isSubmitting}
            />
          </FormFieldWrapper>

          <FormFieldWrapper label="Tipo" required>
            <Select 
              value={formData.type} 
              onValueChange={(value: 'personal' | 'business') => setFormData(prev => ({ ...prev, type: value }))}
              disabled={isSubmitting}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Pessoal</SelectItem>
                <SelectItem value="business">Empresa</SelectItem>
              </SelectContent>
            </Select>
          </FormFieldWrapper>
        </form>
      </ModalBody>

      <ModalFooter
        primaryAction={{
          label: isSubmitting ? 'Criando...' : 'Criar Perfil',
          type: 'submit',
          formId: 'profile-form',
          isLoading: isSubmitting
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: onClose
        }}
      />
    </div>
  );
};
